import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { GOT_TOURNAMENTS } from 'constants/eventConstants';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import { useFeatureFlag, useFeatureFlagExposure } from 'featureFlags';
import { useAppDispatch, useAppSelector } from 'hooks';
import { setSitAndGoList, setTournamentList } from 'reducers/lobby';
import {
  clearSitGoResult,
  rememberSitGoTemplate,
  restoreSitGoResult,
  SitGoResultEntry,
  SitGoTemplate,
  startSitGoResult
} from 'reducers/sitGoResult';
import useGameService from 'services/GameService';
import { decompressData } from 'utils/functions';

import {
  detectSitGoResults,
  openSitGoTournamentIds,
  SIT_GO_BUST_SETTLE_MS,
  toSitGoTemplate
} from './sitGoResultDetection';
import {
  PersistedSitGoResults,
  readPersistedSitGoResults,
  SIT_GO_RESULT_RESTORE_CAP_MS,
  writePersistedSitGoResults
} from './sitGoResultPersistence';

const EMPTY: Record<string, never> = {};

/**
 * EN-11109: starts the Sit & Go result offer when this player's result
 * arrives, at app level — TournamentScreenModal unmounts with its table panel,
 * and the offer must not. Headless; mounted once in App.
 *
 *  - Records the release.sit-go-result-modal exposure the moment a player
 *    reaches a Sit & Go result, for both variants.
 *  - With the flag on: snapshots each open Sit & Go's lobby row as the rematch
 *    template while it is still listed, starts the offer (once per table and
 *    tournament), and drops an entry whose table the player has left. The
 *    offer has no clock: it stays open until then. A bust whose payout has not
 *    landed yet is held briefly first (EN-11139, see `settled`).
 *  - Also with the flag on: keeps the offers in sessionStorage for this
 *    player and puts them back after a page reload (sitGoResultPersistence
 *    has why the feed cannot be relied on for that).
 *  - Also with the flag on: holds the tournament feed of every open Sit & Go
 *    table, on screen or not (see the feed effect below).
 */
const SitGoResultWatcher = () => {
  const enabled = useFeatureFlag('sitGoResultModal');
  const dispatch = useAppDispatch();
  const { socket } = useContext(SocketContext);
  const playerGUID = useAppSelector((state) => state.user.playerGUID) as string;
  const tables = useAppSelector((state) => (state as any).tables) as Record<string, any>;
  const rows = useAppSelector((state) => (state as any).currentTable?.tournamentStatus);
  const sitAndGoList = useAppSelector((state) => (state as any).lobby?.tournament?.sitAndGoList);
  const entries = useAppSelector(
    (state) => (state as any).sitGoResult?.byGameId ?? EMPTY
  ) as Record<string, SitGoResultEntry>;
  const templates = useAppSelector(
    (state) => (state as any).sitGoResult?.templatesByTournamentId ?? EMPTY
  ) as Record<string, SitGoTemplate>;

  const detections = useMemo(
    () => detectSitGoResults(tables, rows, playerGUID),
    [tables, rows, playerGUID]
  );

  useFeatureFlagExposure('sitGoResultModal', 'sit_go_result', detections.length > 0);

  // Keep the lobby's Sit & Go list fresh while a table is on screen. The engine
  // keeps pushing gotTournamentH5 to a socket that has visited the lobby, but
  // only the (unmounted) lobby listened; a rematch must find the next table of
  // its template, which the engine spawns after the player left the lobby.
  useEffect(() => {
    if (!enabled || !socket) return;
    const handleTournaments = (raw: any) => {
      const data = decompressData(raw);
      if (!Array.isArray(data?.response)) return;
      // A null entry is skipped, not read.
      dispatch(setSitAndGoList(data.response.filter((row: any) => row?.tabletype === 't')));
      dispatch(setTournamentList(data.response.filter((row: any) => row?.tabletype === 'm')));
    };
    socket.on(GOT_TOURNAMENTS, handleTournaments);
    return () => {
      socket.off(GOT_TOURNAMENTS, handleTournaments);
    };
  }, [enabled, socket, dispatch]);

  // The result row of a Sit & Go that ends on a table the player is not
  // looking at. App subscribes a tournament's feed only while one of its tables
  // is on screen (visibleTablesForActiveView), the server sends gotTnmStatus
  // only to the subscribers of the moment, and subscribeTournament sends no
  // snapshot: a table that finishes off screen never gets its row, not even
  // when the player switches to it. So the feed of every open Sit & Go table is
  // held here, through GameService's ref-count (App's hold and an info panel's
  // share the one wire subscription), and released when its table goes, when
  // the flag goes off, and on unmount. Diffed as a set, like App's hold, so a
  // table opening or closing never drops and re-opens another table's feed.
  const { subscribeTournamentDetails, unsubscribeTournamentDetails } = useGameService();
  const feedCallsRef = useRef({ subscribeTournamentDetails, unsubscribeTournamentDetails });
  feedCallsRef.current = { subscribeTournamentDetails, unsubscribeTournamentDetails };
  const sitGoFeedsKey = useMemo(
    () => (enabled ? openSitGoTournamentIds(tables).join(',') : ''),
    [enabled, tables]
  );
  const heldFeedsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    // GameService counts nothing without a socket; holding an uncounted id
    // would later release another holder's count.
    if (!socket) return;
    const next = new Set(sitGoFeedsKey ? sitGoFeedsKey.split(',') : []);
    const held = heldFeedsRef.current;
    next.forEach((tournamentId) => {
      if (!held.has(tournamentId)) feedCallsRef.current.subscribeTournamentDetails(tournamentId);
    });
    held.forEach((tournamentId) => {
      if (!next.has(tournamentId)) feedCallsRef.current.unsubscribeTournamentDetails(tournamentId);
    });
    heldFeedsRef.current = next;
  }, [socket, sitGoFeedsKey]);
  useEffect(
    () => () => {
      heldFeedsRef.current.forEach((tournamentId) =>
        feedCallsRef.current.unsubscribeTournamentDetails(tournamentId)
      );
      heldFeedsRef.current = new Set();
    },
    []
  );

  // Template capture: the lobby drops a Sit & Go at 'F', so take its row while
  // the player sits at it.
  useEffect(() => {
    if (!enabled || !Array.isArray(sitAndGoList)) return;
    Object.values(tables || {}).forEach((table: any) => {
      if (table?.game?.tabletype !== 't') return;
      const tournamentId = String(table?.game?.tournamentId ?? '');
      if (!tournamentId || templates[tournamentId]) return;
      const row = sitAndGoList.find((t: any) => String(t?.tournamentId ?? t?.id) === tournamentId);
      const template = row ? toSitGoTemplate(row) : null;
      if (template) dispatch(rememberSitGoTemplate(template));
    });
  }, [enabled, tables, sitAndGoList, templates, dispatch]);

  // Reload: this player's stored offers wait here until their table is back
  // in state.tables (tables restore asynchronously after boot). Holding them
  // outside the slice is what keeps the "table is gone" effect below from
  // clearing them before their table arrives. Loaded once per player, and only
  // while the flag is on.
  const pendingRestoreRef = useRef<{ playerGUID: string; results: PersistedSitGoResults } | null>(
    null
  );
  const loadPendingRestore = useCallback((): PersistedSitGoResults | null => {
    if (!enabled || !playerGUID) return null;
    if (pendingRestoreRef.current?.playerGUID !== playerGUID) {
      pendingRestoreRef.current = {
        playerGUID,
        results: readPersistedSitGoResults(playerGUID, Date.now())
      };
    }
    return pendingRestoreRef.current.results;
  }, [enabled, playerGUID]);

  // Rehydrate a stored offer once its table (same gameID AND tournament) is
  // back. Declared before detection so, in the commit the table arrives, the
  // original offer lands before a re-arriving status row could start a new one.
  useEffect(() => {
    const pending = loadPendingRestore();
    if (!pending) return;
    const nowMs = Date.now();
    Object.entries(pending).forEach(([gameID, stored]) => {
      if (nowMs - stored.entry.startedAtMs > SIT_GO_RESULT_RESTORE_CAP_MS) {
        delete pending[gameID];
        return;
      }
      const tournamentId = String(tables?.[gameID]?.game?.tournamentId ?? '');
      if (!tournamentId || tournamentId === '0') return; // not back (or not hydrated) yet
      delete pending[gameID];
      if (tournamentId !== stored.entry.tournamentId) return; // a later tournament on that gameID
      dispatch(restoreSitGoResult(stored));
    });
  }, [loadPendingRestore, tables, dispatch]);

  // Store the offers for the next reload, together with the ones still waiting
  // for their table, so the empty slice at boot never overwrites them.
  useEffect(() => {
    const pending = loadPendingRestore();
    if (!pending) return;
    const results: PersistedSitGoResults = { ...pending };
    Object.values(entries).forEach((entry) => {
      results[entry.gameID] = {
        entry,
        template: entry.template ?? templates[entry.tournamentId] ?? null
      };
    });
    writePersistedSitGoResults(playerGUID, results);
  }, [loadPendingRestore, playerGUID, entries, templates]);

  // A bust row not yet settled (EN-11139): when it was first seen, keyed by
  // table and tournament, and a tick to come back once its window is up.
  const unsettledSinceRef = useRef(new Map<string, number>());
  const [settleTick, setSettleTick] = useState(0);

  // Start (or enrich) the offer. Dispatch only when the push adds something:
  // tables re-render on every gotTexas.
  useEffect(() => {
    if (!enabled) return undefined;
    const nowMs = Date.now();
    const unsettledSince = unsettledSinceRef.current;
    const stillUnsettled = new Set<string>();
    let wakeInMs = Infinity;
    detections.forEach((detection) => {
      const existing = entries[detection.gameID];
      // Hold a new bust until its row is settled or the window passes, so a
      // paid six-seat bust never opens as "Sorry, you lost" (EN-11139).
      if (!detection.settled && existing?.tournamentId !== detection.tournamentId) {
        const key = `${detection.gameID}:${detection.tournamentId}`;
        stillUnsettled.add(key);
        const since = unsettledSince.get(key) ?? nowMs;
        unsettledSince.set(key, since);
        const remainingMs = since + SIT_GO_BUST_SETTLE_MS - nowMs;
        if (remainingMs > 0) {
          wakeInMs = Math.min(wakeInMs, remainingMs);
          return;
        }
      }
      if (
        existing &&
        existing.tournamentId === detection.tournamentId &&
        detection.outcome.prize <= existing.outcome.prize &&
        (!detection.tableFinished || existing.tableFinished) &&
        (existing.outcome.capacity || !detection.outcome.capacity)
      ) {
        return;
      }
      dispatch(startSitGoResult({ ...detection, startedAtMs: Date.now() }));
    });
    Array.from(unsettledSince.keys()).forEach((key) => {
      if (!stillUnsettled.has(key)) unsettledSince.delete(key);
    });
    if (wakeInMs === Infinity) return undefined;
    const timer = window.setTimeout(() => setSettleTick((tick) => tick + 1), wakeInMs);
    return () => window.clearTimeout(timer);
  }, [enabled, detections, entries, dispatch, settleTick]);

  // The table is gone (exit, removal, logout): the offer goes with it.
  useEffect(() => {
    Object.keys(entries).forEach((gameID) => {
      if (!tables?.[gameID]) dispatch(clearSitGoResult(gameID));
    });
  }, [entries, tables, dispatch]);

  return null;
};

export default SitGoResultWatcher;
